/**
 * Teaching AI Assistant — upload PDF/PPT/TXT or paste text →
 * Claude generates Lesson Plan + Quiz + Homework in one shot.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import {
  Sparkles, Upload, ArrowLeft, AlertCircle, Loader2, Check, Copy, Printer,
  BookOpen, ClipboardList, Clock, FileText, X, Save, Share2, Languages,
} from "lucide-react";
import PremiumNav from "@/components/premium/PremiumNav";
import PremiumFooter from "@/components/premium/PremiumFooter";
import { PremiumCard, PremiumButton, SectionHeading, Pill } from "@/components/premium/Primitives";
import AIProgressLoader from "@/components/premium/AIProgressLoader";

const API = process.env.REACT_APP_BACKEND_URL || '';
const MAX_BYTES = 20 * 1024 * 1024;
const ACCEPTED = /\.(pdf|pptx?|txt)$/i;

const TABS = [
  { id: "lesson_plan", label: "Lesson Plan", Icon: BookOpen },
  { id: "quiz", label: "Quiz", Icon: ClipboardList },
  { id: "homework", label: "Homework", Icon: Clock },
];

export default function TeachingAIAssistant() {
  const navigate = useNavigate();
  const [mode, setMode] = useState("file"); // file | text
  const [file, setFile] = useState(null);
  const [text, setText] = useState("");
  const [title, setTitle] = useState("");
  const [grade, setGrade] = useState("8");
  const [subject, setSubject] = useState("");
  const [durationMin, setDurationMin] = useState(45);
  const [homeworkDays, setHomeworkDays] = useState(3);
  const [quizMcq, setQuizMcq] = useState(8);
  const [quizShort, setQuizShort] = useState(3);
  const [language, setLanguage] = useState("english");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [activeTab, setActiveTab] = useState("lesson_plan");
  const [copied, setCopied] = useState(false);
  const [savedBundleId, setSavedBundleId] = useState(null);
  const [shareUrl, setShareUrl] = useState("");
  const [saving, setSaving] = useState(false);
  // Bulk-upload state
  const [bulkFiles, setBulkFiles] = useState([]);
  const [bulkQueue, setBulkQueue] = useState([]); // [{name, status, bundleId, err}]
  const [bulkRunning, setBulkRunning] = useState(false);

  const resetAll = () => {
    setResult(null); setError(""); setFile(null); setText(""); setTitle("");
    setSavedBundleId(null); setShareUrl(""); setBulkFiles([]); setBulkQueue([]);
  };

  const onPickBulk = (e) => {
    const arr = Array.from(e.target.files || []).filter((f) => ACCEPTED.test(f.name) && f.size <= MAX_BYTES);
    if (arr.length === 0) { setError("Pick at least one valid PDF/PPTX/TXT (≤20MB each)."); return; }
    setError(""); setBulkFiles(arr.slice(0, 10));
  };

  const runBulk = async () => {
    if (bulkFiles.length === 0) { setError("Pick files first."); return; }
    setBulkRunning(true); setError("");
    const queue = bulkFiles.map((f) => ({ name: f.name, status: "pending", bundleId: null, err: "" }));
    setBulkQueue(queue);
    for (let i = 0; i < bulkFiles.length; i++) {
      queue[i].status = "running";
      setBulkQueue([...queue]);
      try {
        const fd = new FormData();
        fd.append("file", bulkFiles[i]);
        fd.append("grade", grade);
        if (subject) fd.append("subject", subject);
        fd.append("duration_min", String(durationMin));
        fd.append("homework_days", String(homeworkDays));
        fd.append("quiz_mcq", String(quizMcq));
        fd.append("quiz_short", String(quizShort));
        fd.append("language", language);
        const r = await axios.post(`${API}/api/teaching/ai-assistant`, fd, {
          withCredentials: true,
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 180000,
        });
        const res = r.data?.result || r.data;
        // Auto-save bundle
        try {
          const s = await axios.post(`${API}/api/teaching/bundles`, {
            title: res.title || bulkFiles[i].name,
            grade: res.grade || grade,
            subject: res.subject || subject,
            language: res.language || language,
            source: res.source || bulkFiles[i].name,
            bundle: res,
          }, { withCredentials: true });
          queue[i].bundleId = s.data?.id;
          queue[i].status = "done";
        } catch (_e) {
          queue[i].status = "generated_not_saved";
        }
      } catch (e) {
        queue[i].status = "error";
        queue[i].err = e?.response?.data?.detail || e?.message || "Failed";
      }
      setBulkQueue([...queue]);
    }
    setBulkRunning(false);
  };

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!ACCEPTED.test(f.name)) {
      setError("Only .pdf, .pptx, .ppt or .txt files are accepted.");
      return;
    }
    if (f.size > MAX_BYTES) {
      setError("File too large. Max 20 MB.");
      return;
    }
    setError("");
    setFile(f);
  };

  const run = async () => {
    if (mode === "file" && !file) {
      setError("Choose a file to upload, or switch to Paste text.");
      return;
    }
    if (mode === "text" && !text.trim()) {
      setError("Paste some study material first.");
      return;
    }
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const fd = new FormData();
      if (mode === "file") fd.append("file", file);
      else fd.append("text", text);
      if (title) fd.append("title", title);
      fd.append("grade", grade);
      if (subject) fd.append("subject", subject);
      fd.append("duration_min", String(durationMin));
      fd.append("homework_days", String(homeworkDays));
      fd.append("quiz_mcq", String(quizMcq));
      fd.append("quiz_short", String(quizShort));
      fd.append("language", language);

      const r = await axios.post(`${API}/api/teaching/ai-assistant`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180000,
      });
      setResult(r.data?.result || r.data);
      setActiveTab("lesson_plan");
      setSavedBundleId(null);
      setShareUrl("");
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Generation failed");
    } finally {
      setLoading(false);
    }
  };

  const copyJson = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(result, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_e) { /* clipboard unavailable */ }
  };

  const saveBundle = async () => {
    if (!result || saving) return;
    setSaving(true);
    try {
      const r = await axios.post(`${API}/api/teaching/bundles`, {
        title: result.title || "Untitled bundle",
        grade: result.grade || grade,
        subject: result.subject || subject,
        language: result.language || language,
        source: result.source || "pasted-text",
        bundle: result,
      }, { withCredentials: true });
      setSavedBundleId(r.data?.id);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Save failed";
      setError(detail.includes("Login") ? "Please log in to save bundles." : detail);
    } finally {
      setSaving(false);
    }
  };

  const shareBundle = async () => {
    if (!savedBundleId) return;
    try {
      const r = await axios.post(`${API}/api/teaching/bundles/${savedBundleId}/share`, {}, { withCredentials: true });
      // Use OG URL so WhatsApp / Twitter / Facebook generate rich previews
      const suffix = r.data?.og_url_suffix || r.data?.url_suffix || `/share/bundle/${r.data?.share_id}`;
      const fullUrl = `${window.location.origin}${suffix}`;
      setShareUrl(fullUrl);
      try { await navigator.clipboard.writeText(fullUrl); } catch (_e) { /* ignore */ }
    } catch (_e) { /* ignore */ }
  };

  return (
    <div className="min-h-screen bg-white font-sans" data-testid="teaching-ai-assistant">
      <PremiumNav />
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
        <button
          onClick={() => navigate("/teaching")}
          className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#A855F7] mb-6"
          data-testid="ai-assistant-back"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Teaching Suite
        </button>

        <SectionHeading
          eyebrow="Teaching AI Assistant"
          title="Upload material → Lesson Plan + Quiz + Homework"
          subtitle="One file, one click. Claude reads your chapter, slides or notes and builds a complete classroom bundle in ~30 seconds."
        />

        {!result && (
          <PremiumCard className="mt-8 p-6 sm:p-8">
            {/* Mode tabs */}
            <div className="flex items-center gap-2 p-1 bg-slate-100 rounded-xl w-fit mb-6">
              {[
                { id: "file", label: "Upload file", Icon: Upload },
                { id: "text", label: "Paste text", Icon: FileText },
                { id: "bulk", label: "Bulk PDFs", Icon: Upload },
              ].map((m) => (
                <button
                  key={m.id}
                  onClick={() => { setMode(m.id); setError(""); }}
                  data-testid={`mode-${m.id}`}
                  className={`flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm font-medium transition-all ${
                    mode === m.id
                      ? "bg-white text-[#111111] shadow-sm"
                      : "text-slate-500 hover:text-slate-700"
                  }`}
                >
                  <m.Icon className="w-3.5 h-3.5" />
                  {m.label}
                </button>
              ))}
            </div>

            {/* Mode body */}
            {mode === "file" && (
              <label
                htmlFor="ai-assistant-file"
                data-testid="ai-assistant-dropzone"
                className="block border-2 border-dashed border-slate-300 hover:border-[#A855F7] rounded-2xl p-8 text-center cursor-pointer transition-colors bg-slate-50/40"
              >
                <input
                  id="ai-assistant-file"
                  type="file"
                  accept=".pdf,.pptx,.ppt,.txt"
                  onChange={onPick}
                  className="hidden"
                  data-testid="ai-assistant-file-input"
                />
                {file ? (
                  <div className="flex items-center justify-center gap-3">
                    <FileText className="w-6 h-6 text-[#A855F7]" />
                    <span className="text-sm font-medium text-slate-800">{file.name}</span>
                    <button
                      type="button"
                      onClick={(e) => { e.preventDefault(); setFile(null); }}
                      className="text-slate-400 hover:text-red-500"
                      data-testid="ai-assistant-clear-file"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <>
                    <Upload className="w-8 h-8 text-slate-400 mx-auto mb-3" />
                    <p className="text-sm font-medium text-slate-700">Drop your file here or click to browse</p>
                    <p className="text-xs text-slate-500 mt-1">PDF · PPTX · TXT · Max 20 MB</p>
                  </>
                )}
              </label>
            )}
            {mode === "text" && (
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Paste a chapter, lecture notes, slide content or any study material here (up to ~16,000 characters)…"
                rows={8}
                className="w-full px-4 py-3 border border-slate-300 rounded-xl text-sm font-sans focus:ring-2 focus:ring-[#A855F7] focus:border-transparent outline-none resize-y"
                data-testid="ai-assistant-text"
              />
            )}
            {mode === "bulk" && (
              <div data-testid="ai-assistant-bulk">
                <label htmlFor="ai-assistant-bulk-file" className="block border-2 border-dashed border-slate-300 hover:border-[#A855F7] rounded-2xl p-6 text-center cursor-pointer transition-colors bg-slate-50/40">
                  <input
                    id="ai-assistant-bulk-file"
                    type="file"
                    accept=".pdf,.pptx,.ppt,.txt"
                    multiple
                    onChange={onPickBulk}
                    className="hidden"
                    data-testid="ai-assistant-bulk-input"
                  />
                  <Upload className="w-7 h-7 text-slate-400 mx-auto mb-2" />
                  <p className="text-sm font-medium text-slate-700">Select up to 10 files</p>
                  <p className="text-xs text-slate-500 mt-1">Each file → 1 auto-saved bundle. PDF / PPTX / TXT · 20MB each</p>
                  {bulkFiles.length > 0 && (
                    <p className="text-xs text-[#A855F7] mt-2 font-medium">{bulkFiles.length} file(s) queued</p>
                  )}
                </label>
                {bulkQueue.length > 0 && (
                  <div className="mt-4 space-y-2" data-testid="ai-assistant-bulk-queue">
                    {bulkQueue.map((q, i) => (
                      <div key={i} className="flex items-center gap-2 p-2.5 rounded-lg bg-slate-50 border border-slate-200">
                        {q.status === "pending" && <Clock className="w-3.5 h-3.5 text-slate-400"/>}
                        {q.status === "running" && <Loader2 className="w-3.5 h-3.5 animate-spin text-[#A855F7]"/>}
                        {(q.status === "done" || q.status === "generated_not_saved") && <Check className="w-3.5 h-3.5 text-green-600"/>}
                        {q.status === "error" && <AlertCircle className="w-3.5 h-3.5 text-red-500"/>}
                        <span className="text-xs flex-1 truncate text-slate-700">{q.name}</span>
                        <span className="text-[10px] uppercase tracking-wide text-slate-500">{q.status.replace(/_/g, " ")}</span>
                        {q.bundleId && (
                          <a href={`/teaching/bundles/${q.bundleId}`} className="text-[11px] text-[#A855F7] hover:underline" target="_blank" rel="noopener noreferrer">view</a>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Settings grid */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
              <Field label="Lesson title (optional)">
                <input
                  value={title} onChange={(e) => setTitle(e.target.value)}
                  placeholder="Auto-derive from content"
                  data-testid="ai-assistant-title"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-[#A855F7] focus:border-transparent outline-none"
                />
              </Field>
              <Field label="Grade">
                <select
                  value={grade} onChange={(e) => setGrade(e.target.value)}
                  data-testid="ai-assistant-grade"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-[#A855F7] focus:border-transparent"
                >
                  {["1","2","3","4","5","6","7","8","9","10","11","12","UG"].map((g) => (
                    <option key={g} value={g}>Grade {g}</option>
                  ))}
                </select>
              </Field>
              <Field label="Subject (optional)">
                <input
                  value={subject} onChange={(e) => setSubject(e.target.value)}
                  placeholder="e.g. Biology"
                  data-testid="ai-assistant-subject"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-[#A855F7] focus:border-transparent outline-none"
                />
              </Field>
              <Field label="Lesson duration (min)">
                <input
                  type="number" min={20} max={120}
                  value={durationMin} onChange={(e) => setDurationMin(parseInt(e.target.value) || 45)}
                  data-testid="ai-assistant-duration"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-[#A855F7] focus:border-transparent outline-none"
                />
              </Field>
              <Field label="MCQ questions">
                <input
                  type="number" min={0} max={20}
                  value={quizMcq} onChange={(e) => setQuizMcq(parseInt(e.target.value) || 0)}
                  data-testid="ai-assistant-mcq"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-[#A855F7] focus:border-transparent outline-none"
                />
              </Field>
              <Field label="Short answer questions">
                <input
                  type="number" min={0} max={10}
                  value={quizShort} onChange={(e) => setQuizShort(parseInt(e.target.value) || 0)}
                  data-testid="ai-assistant-short"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-[#A855F7] focus:border-transparent outline-none"
                />
              </Field>
              <Field label="Homework days">
                <input
                  type="number" min={1} max={14}
                  value={homeworkDays} onChange={(e) => setHomeworkDays(parseInt(e.target.value) || 3)}
                  data-testid="ai-assistant-days"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-[#A855F7] focus:border-transparent outline-none"
                />
              </Field>
              <Field label="Output language">
                <select
                  value={language} onChange={(e) => setLanguage(e.target.value)}
                  data-testid="ai-assistant-language"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-[#A855F7] focus:border-transparent"
                >
                  <option value="english">English</option>
                  <option value="hindi">हिंदी (Hindi)</option>
                  <option value="hinglish">Hinglish</option>
                </select>
              </Field>
            </div>

            {error && (
              <div className="mt-5 flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700" data-testid="ai-assistant-error">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /> {error}
              </div>
            )}

            <div className="mt-6 flex items-center justify-between flex-wrap gap-3">
              <span className="text-xs text-slate-500">~30-60 seconds · cached for repeat use</span>
              {mode === "bulk" ? (
                <PremiumButton onClick={runBulk} disabled={bulkRunning || bulkFiles.length === 0} data-testid="ai-assistant-bulk-run">
                  {bulkRunning ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Processing {bulkQueue.filter(q=>q.status==='running').length+1}/{bulkFiles.length}…</>
                  ) : (
                    <><Sparkles className="w-4 h-4" /> Generate {bulkFiles.length} bundle{bulkFiles.length===1?'':'s'}</>
                  )}
                </PremiumButton>
              ) : (
                <PremiumButton onClick={run} disabled={loading} data-testid="ai-assistant-run">
                  {loading ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Generating bundle…</>
                  ) : (
                    <><Sparkles className="w-4 h-4" /> Generate full bundle</>
                  )}
                </PremiumButton>
              )}
            </div>
          </PremiumCard>
        )}

        {loading && mode !== "bulk" && (
          <div className="mt-6" data-testid="teaching-loading">
            <AIProgressLoader
              visible={loading}
              label="building your teaching bundle"
              stages={[
                "reading your input material",
                "drafting the lesson plan",
                "generating quiz questions",
                "writing homework and notes",
              ]}
            />
          </div>
        )}

        {result && (
          <div className="mt-8 space-y-5">
            {/* Header */}
            <PremiumCard className="p-6">
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                  <h2 className="text-2xl font-heading font-bold text-[#111111]" data-testid="ai-assistant-result-title">
                    {result.title || "Generated bundle"}
                  </h2>
                  <div className="mt-2 flex items-center gap-2 flex-wrap">
                    <Pill variant="brand">{result.grade ? `Grade ${result.grade}` : "Grade —"}</Pill>
                    <Pill variant="neutral">{result.subject || "General"}</Pill>
                    <Pill variant="outline">{result.page_count || 0} page(s)</Pill>
                    {result.language && result.language !== "english" && (
                      <Pill variant="brand"><Languages className="w-3 h-3 inline -mt-0.5 mr-1"/>{result.language === "hindi" ? "हिंदी" : "Hinglish"}</Pill>
                    )}
                    {savedBundleId && <Pill variant="dark">Saved</Pill>}
                    {result.source && (
                      <span className="text-xs text-slate-500 truncate max-w-xs">Source: {result.source}</span>
                    )}
                  </div>
                  {Array.isArray(result.key_topics) && result.key_topics.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {result.key_topics.map((t, i) => (
                        <span key={i} className="px-2 py-0.5 text-xs bg-slate-100 text-slate-700 rounded-md">{t}</span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex gap-2 flex-wrap">
                  <button onClick={copyJson} data-testid="ai-assistant-copy" className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-slate-300 rounded-lg hover:bg-slate-50">
                    {copied ? <Check className="w-3.5 h-3.5 text-green-600"/> : <Copy className="w-3.5 h-3.5"/>}
                    {copied ? "Copied" : "Copy JSON"}
                  </button>
                  <button onClick={() => window.print()} data-testid="ai-assistant-print" className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-slate-300 rounded-lg hover:bg-slate-50">
                    <Printer className="w-3.5 h-3.5"/> Print
                  </button>
                  {!savedBundleId ? (
                    <button onClick={saveBundle} disabled={saving} data-testid="ai-assistant-save" className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50">
                      {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Save className="w-3.5 h-3.5"/>} Save bundle
                    </button>
                  ) : (
                    <button onClick={shareBundle} data-testid="ai-assistant-share" className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-slate-300 rounded-lg hover:bg-slate-50">
                      <Share2 className="w-3.5 h-3.5"/> {shareUrl ? "Copied!" : "Share link"}
                    </button>
                  )}
                  <button onClick={resetAll} data-testid="ai-assistant-new" className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-[#A855F7] text-white rounded-lg hover:bg-[#9333EA]">
                    New bundle
                  </button>
                </div>
              </div>

              {/* Tabs */}
              <div className="mt-6 flex items-center gap-2 border-b border-slate-200">
                {TABS.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => setActiveTab(t.id)}
                    data-testid={`ai-tab-${t.id}`}
                    className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                      activeTab === t.id
                        ? "border-[#A855F7] text-[#A855F7]"
                        : "border-transparent text-slate-500 hover:text-slate-700"
                    }`}
                  >
                    <t.Icon className="w-4 h-4" /> {t.label}
                  </button>
                ))}
              </div>
            </PremiumCard>

            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
            >
              {activeTab === "lesson_plan" && <LessonPlanView lp={result.lesson_plan} />}
              {activeTab === "quiz" && <QuizView q={result.quiz} />}
              {activeTab === "homework" && <HomeworkView hw={result.homework} />}
            </motion.div>
          </div>
        )}
      </div>
      <PremiumFooter />
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-slate-500 mb-1.5">{label}</span>
      {children}
    </label>
  );
}

function LessonPlanView({ lp }) {
  if (!lp) return <Empty msg="Lesson plan not available." />;
  return (
    <PremiumCard className="p-6" data-testid="lp-view">
      <h3 className="font-heading text-xl font-bold text-[#111111]">{lp.title}</h3>
      <p className="text-xs text-slate-500 mt-1">Duration: {lp.duration_min} min</p>

      <Section title="Learning objectives">
        <ul className="list-disc pl-5 space-y-1">
          {(lp.learning_objectives || []).map((o, i) => <li key={i} className="text-sm text-slate-700">{o}</li>)}
        </ul>
      </Section>

      {Array.isArray(lp.prerequisites) && lp.prerequisites.length > 0 && (
        <Section title="Prerequisites">
          <ul className="list-disc pl-5 space-y-1">
            {lp.prerequisites.map((p, i) => <li key={i} className="text-sm text-slate-700">{p}</li>)}
          </ul>
        </Section>
      )}

      {Array.isArray(lp.materials) && (
        <Section title="Materials needed">
          <div className="flex flex-wrap gap-2">
            {lp.materials.map((m, i) => (
              <span key={i} className="px-2.5 py-1 text-xs bg-slate-100 rounded-md">{m}</span>
            ))}
          </div>
        </Section>
      )}

      {Array.isArray(lp.vocabulary) && lp.vocabulary.length > 0 && (
        <Section title="Vocabulary">
          <div className="grid sm:grid-cols-2 gap-2">
            {lp.vocabulary.map((v, i) => (
              <div key={i} className="p-3 rounded-lg bg-slate-50 border border-slate-200">
                <div className="text-sm font-semibold text-[#111111]">{v.term}</div>
                <div className="text-xs text-slate-600 mt-0.5">{v.definition}</div>
              </div>
            ))}
          </div>
        </Section>
      )}

      <Section title="Lesson structure">
        <div className="space-y-2.5">
          {(lp.lesson_structure || []).map((s, i) => (
            <div key={i} className="p-4 rounded-xl border border-slate-200 bg-white">
              <div className="flex items-center justify-between mb-1.5">
                <span className="font-medium text-[#111111]">{s.phase}</span>
                <Pill variant="brand">{s.duration_min} min</Pill>
              </div>
              <p className="text-sm text-slate-700">{s.activity}</p>
              <div className="mt-2 grid sm:grid-cols-2 gap-2 text-xs text-slate-600">
                <div><b>Teacher:</b> {s.teacher_action}</div>
                <div><b>Student:</b> {s.student_action}</div>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {lp.differentiation && (
        <Section title="Differentiation">
          <div className="grid sm:grid-cols-3 gap-2 text-sm">
            {["struggling","advanced","ell"].map((k) => (
              <div key={k} className="p-3 rounded-lg bg-slate-50 border border-slate-200">
                <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">{k}</div>
                <div className="text-slate-700">{lp.differentiation[k]}</div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {lp.assessment_strategy && (
        <Section title="Assessment strategy">
          <p className="text-sm text-slate-700">{lp.assessment_strategy}</p>
        </Section>
      )}
    </PremiumCard>
  );
}

function QuizView({ q }) {
  if (!q) return <Empty msg="Quiz not available." />;
  return (
    <PremiumCard className="p-6" data-testid="quiz-view">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading text-xl font-bold text-[#111111]">{q.title}</h3>
        <Pill variant="brand">{q.total_marks ?? "?"} marks</Pill>
      </div>

      {Array.isArray(q.mcq) && q.mcq.length > 0 && (
        <Section title={`Section A — Multiple Choice (${q.mcq.length})`}>
          <div className="space-y-4">
            {q.mcq.map((m, i) => (
              <div key={i} className="p-4 rounded-xl border border-slate-200 bg-white">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold text-[#111111]">Q{i + 1}. {m.q}</span>
                  <div className="flex items-center gap-2 shrink-0">
                    {m.page_ref && <span className="text-[10px] px-1.5 py-0.5 bg-slate-100 rounded text-slate-500">p.{m.page_ref}</span>}
                    <Pill variant="outline">{m.marks || 1}m</Pill>
                  </div>
                </div>
                <ul className="space-y-1 mt-2">
                  {(m.options || []).map((o, j) => (
                    <li key={j} className={`text-sm pl-2 ${o.startsWith(m.answer) ? "text-green-700 font-medium" : "text-slate-700"}`}>{o}</li>
                  ))}
                </ul>
                <div className="mt-2 text-xs text-slate-500">
                  <span className="font-medium">Answer: {m.answer}</span>{m.explanation ? ` — ${m.explanation}` : ""}
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {Array.isArray(q.short) && q.short.length > 0 && (
        <Section title={`Section B — Short Answers (${q.short.length})`}>
          <div className="space-y-4">
            {q.short.map((s, i) => (
              <div key={i} className="p-4 rounded-xl border border-slate-200 bg-white">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold text-[#111111]">Q{i + 1}. {s.q}</span>
                  <div className="flex items-center gap-2 shrink-0">
                    {s.page_ref && <span className="text-[10px] px-1.5 py-0.5 bg-slate-100 rounded text-slate-500">p.{s.page_ref}</span>}
                    <Pill variant="outline">{s.marks || 3}m</Pill>
                  </div>
                </div>
                <div className="mt-1 text-xs uppercase tracking-wide text-slate-400">Model answer</div>
                <p className="text-sm text-slate-700 mt-1">{s.answer}</p>
              </div>
            ))}
          </div>
        </Section>
      )}
    </PremiumCard>
  );
}

function HomeworkView({ hw }) {
  if (!hw) return <Empty msg="Homework not available." />;
  return (
    <PremiumCard className="p-6" data-testid="homework-view">
      <h3 className="font-heading text-xl font-bold text-[#111111]">Homework schedule</h3>
      <div className="mt-2 flex items-center gap-2 flex-wrap">
        <Pill variant="brand">{hw.total_days} days</Pill>
        <Pill variant="neutral">~{hw.minutes_per_day || 25} min/day</Pill>
      </div>

      {hw.weekly_goal && (
        <div className="mt-4 p-3 rounded-lg bg-brand-50/50 border border-brand-200 text-sm text-slate-700">
          <b className="text-[#7E22CE]">Goal:</b> {hw.weekly_goal}
        </div>
      )}

      <Section title="Daily plan">
        <div className="space-y-3">
          {(hw.days || []).map((d, i) => (
            <div key={i} className="p-4 rounded-xl border border-slate-200 bg-white">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-[#111111]">Day {d.day}</span>
                <span className="text-xs text-slate-500">{d.focus}</span>
              </div>
              <ul className="space-y-1.5">
                {(d.tasks || []).map((t, j) => (
                  <li key={j} className="flex items-start gap-2 text-sm">
                    <span className="text-[10px] uppercase font-bold text-[#A855F7] tracking-wide mt-1 shrink-0">{t.type}</span>
                    <span className="flex-1 text-slate-700">{t.task}</span>
                    <span className="text-xs text-slate-400 shrink-0">{t.time_min}m</span>
                  </li>
                ))}
              </ul>
              {d.parent_note && (
                <div className="mt-2 text-xs text-slate-500 italic border-l-2 border-slate-200 pl-2">
                  Parent note: {d.parent_note}
                </div>
              )}
            </div>
          ))}
        </div>
      </Section>
    </PremiumCard>
  );
}

function Section({ title, children }) {
  return (
    <div className="mt-5">
      <h4 className="text-xs uppercase tracking-[0.15em] font-bold text-slate-400 mb-2">{title}</h4>
      {children}
    </div>
  );
}

function Empty({ msg }) {
  return (
    <PremiumCard className="p-8 text-center text-sm text-slate-500">{msg}</PremiumCard>
  );
}
